/**
 * Spotify OAuth Authentication Service
 * 
 * Handles Spotify OAuth 2.0 PKCE flow for Web API access
 */

const SPOTIFY_CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;

// Determine redirect URI:
// 1. Check environment variable first (for custom URLs like ngrok)
// 2. Use production URL if not localhost
// 3. For localhost, use localhost (must be added to Spotify app settings)
const getRedirectURI = () => {
  // Check for custom redirect URI in environment (for ngrok or other custom URLs)
  if (process.env.REACT_APP_SPOTIFY_REDIRECT_URI) {
    return process.env.REACT_APP_SPOTIFY_REDIRECT_URI;
  }
  
  // Production or staging
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.origin}/services`;
  }
  
  // Local development - use actual hostname (localhost or 127.0.0.1) and port
  const hostname = window.location.hostname; // Will be 'localhost' or '127.0.0.1'
  const port = window.location.port || '3000';
  const redirectUri = `http://${hostname}:${port}/services`;
  console.log('📍 Using localhost redirect URI for Spotify OAuth:', redirectUri);
  console.log('   Make sure this EXACT URI is added to your Spotify app settings:');
  console.log('   https://developer.spotify.com/dashboard → Your App → Edit Settings → Redirect URIs');
  
  return redirectUri;
};

const REDIRECT_URI = getRedirectURI();
const SCOPES = [
  'user-read-private',
  'user-read-email',
  'playlist-read-private',
  'playlist-read-collaborative',
  'user-library-read',
  'user-top-read',
  'user-read-recently-played',
  'streaming',
  'user-read-playback-state',
  'user-modify-playback-state',
].join(' ');

// Storage keys
const TOKEN_KEY = 'jookbox_spotify_token';
const TOKEN_EXPIRY_KEY = 'jookbox_spotify_token_expiry';
const REFRESH_TOKEN_KEY = 'jookbox_spotify_refresh_token';
const CODE_VERIFIER_KEY = 'jookbox_spotify_code_verifier';

class SpotifyAuthService {
  constructor() {
    this.accessToken = localStorage.getItem(TOKEN_KEY);
    this.tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Generate random string for PKCE
   */
  generateRandomString(length) {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], '');
  }

  /**
   * Generate code challenge for PKCE
   */
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  /**
   * Check if user is authenticated with Spotify
   */
  isAuthenticated() {
    if (!this.accessToken) return false;
    
    if (this.tokenExpiry) {
      const expiry = new Date(this.tokenExpiry);
      if (expiry < new Date()) {
        // Token expired, try to refresh
        return false;
      }
    }
    
    return true;
  }

  /**
   * Get the current access token
   */
  getAccessToken() {
    if (this.isAuthenticated()) {
      return this.accessToken;
    }
    return null;
  }

  /**
   * Initiate Spotify OAuth flow (PKCE)
   */
  async signIn() {
    if (!SPOTIFY_CLIENT_ID) {
      throw new Error('Spotify Client ID not configured. Please set REACT_APP_SPOTIFY_CLIENT_ID in your .env file.');
    }

    // Get redirect URI (dynamic to handle ngrok changes)
    const redirectUri = getRedirectURI();
    console.log('🔐 Spotify OAuth Configuration:');
    console.log('   Redirect URI:', redirectUri);
    console.log('   Client ID:', SPOTIFY_CLIENT_ID);
    console.log('   Current URL:', window.location.href);
    
    // Note: localhost HTTP works fine with Spotify, just needs to be in app settings
    if (redirectUri.includes('localhost') && !redirectUri.includes('https')) {
      console.log('ℹ️ Using localhost for Spotify OAuth (this is fine, no ngrok needed)');
      console.log('   Just make sure this redirect URI is added to your Spotify app:');
      console.log('   ', redirectUri);
    }

    // Generate PKCE values
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later
    localStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: SCOPES,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      show_dialog: 'true'
    });

    const authUrl = `https://accounts.spotify.com/authorize?${params.toString()}`;
    console.log('🔐 Redirecting to Spotify authorization...');
    console.log('   Authorization URL:', authUrl);
    console.log('   ⚠️ Make sure this redirect URI matches your Spotify app settings:');
    console.log('      ', redirectUri);
    
    // Redirect to Spotify
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback - exchange code for token
   */
  async handleCallback(code) {
    const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY);
    
    if (!codeVerifier) {
      throw new Error('No code verifier found. Please try signing in again.');
    }

    const redirectUri = getRedirectURI();
    console.log('🔄 Exchanging code for token...');
    console.log('   Redirect URI:', redirectUri);

    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: SPOTIFY_CLIENT_ID,
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      let errorMessage = error.error_description || error.error || 'Failed to exchange code for token';
      
      // Provide helpful error message for invalid redirect URI
      if (error.error === 'invalid_grant' || error.error === 'INVALID_CLIENT' || error.error_description?.includes('redirect_uri') || error.error_description?.includes('Invalid redirect')) {
        errorMessage = `❌ Invalid redirect URI error!\n\n` +
          `The redirect URI being used: ${redirectUri}\n\n` +
          `This must EXACTLY match one of the redirect URIs configured in your Spotify app:\n\n` +
          `To fix this:\n` +
          `1. Go to https://developer.spotify.com/dashboard\n` +
          `2. Select your app\n` +
          `3. Click "Edit Settings"\n` +
          `4. Add this EXACT redirect URI to "Redirect URIs":\n` +
          `   ${redirectUri}\n\n` +
          `OR set REACT_APP_SPOTIFY_REDIRECT_URI in .env to match an existing redirect URI.\n\n` +
          `Note: The redirect URI must match EXACTLY (including http vs https, trailing slashes, etc.)`;
      }
      
      console.error('❌ Spotify token exchange error:', error);
      console.error('   Redirect URI used:', redirectUri);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Store tokens
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;
    this.tokenExpiry = new Date(Date.now() + data.expires_in * 1000).toISOString();
    
    localStorage.setItem(TOKEN_KEY, this.accessToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry);
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
    }
    
    // Clean up code verifier
    localStorage.removeItem(CODE_VERIFIER_KEY);
    
    console.log('✅ Spotify authenticated successfully');
    
    return {
      accessToken: this.accessToken,
      expiresIn: data.expires_in
    };
  }

  /**
   * Refresh the access token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: SPOTIFY_CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: this.refreshToken,
      }),
    });

    if (!response.ok) {
      this.clearToken();
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    
    this.accessToken = data.access_token;
    this.tokenExpiry = new Date(Date.now() + data.expires_in * 1000).toISOString();
    
    localStorage.setItem(TOKEN_KEY, this.accessToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry);
    
    if (data.refresh_token) {
      this.refreshToken = data.refresh_token;
      localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
    }

    return this.accessToken;
  }

  /**
   * Sign out from Spotify
   */
  signOut() {
    this.clearToken();
  }

  /**
   * Clear stored tokens
   */
  clearToken() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(CODE_VERIFIER_KEY);
  }

  /**
   * Get user profile from Spotify
   */
  async getUserProfile() {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Spotify');
    }

    const response = await fetch('https://api.spotify.com/v1/me', {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh
        try {
          await this.refreshAccessToken();
          return this.getUserProfile();
        } catch {
          this.clearToken();
          throw new Error('Spotify session expired. Please sign in again.');
        }
      }
      throw new Error('Failed to fetch Spotify user profile');
    }

    const data = await response.json();
    
    return {
      id: data.id,
      name: data.display_name,
      email: data.email,
      image: data.images?.[0]?.url,
      product: data.product, // 'premium', 'free', etc.
      country: data.country
    };
  }
}

// Export singleton instance
export const spotifyAuth = new SpotifyAuthService();
export default spotifyAuth;

